import { useState, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Renders children into <body> instead of inline in the React tree.
 *
 * WHY: the app's scroll container `.main-content` uses
 * `overflow-y:auto; -webkit-overflow-scrolling:touch`. On iOS WebKit a momentum-
 * scroll container becomes the containing block for its `position:fixed`
 * descendants, so a fixed drawer/modal rendered inside a page gets trapped in
 * `.main-content` and paints BELOW the fixed `.bottom-nav` (which lives outside
 * it). Portaling the overlay to <body> takes it out of that container, so it
 * behaves as a real viewport overlay and its z-index wins — on iPhone too.
 *
 * Use for any full-screen / bottom-sheet `position:fixed` overlay (drawers,
 * modals, photo viewers, confirmation dialogs).
 */
export function Portal({ children }: { children: ReactNode }) {
  const [host] = useState(() => document.createElement('div'));
  useEffect(() => {
    document.body.appendChild(host);
    return () => { document.body.removeChild(host); };
  }, [host]);
  return createPortal(children, host);
}
